import React from 'react';
import styled from '@emotion/styled';
import { ChevronRight, AlertTriangle, Lightbulb } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

// ─── Section ────────────────────────────────────────────────────────

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ─── Card Row ───────────────────────────────────────────────────────

const CardRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: white;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const CardTitle = styled.span`
  flex: 1;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CountBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${p => p.$color};
  flex-shrink: 0;
`;

const QualityBadge = styled.span`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
`;

const ChevronIcon = styled.span`
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

/**
 * DashboardPivotCards — Flat clickable pivot list.
 * Each row shows title + count badges; clicking opens pivot detail.
 */
export default function DashboardPivotCards({
  pivotGroups = [],
  onPivotClick,
}) {
  const { t } = useTranslation();
  if (pivotGroups.length === 0) return null;

  return (
    <div>
      <SectionTitle>{t('lenses.pivotPointsSection', { count: pivotGroups.length })}</SectionTitle>
      <CardsStack>
        {pivotGroups.map(pg => (
          <CardRow key={pg.pivotId} onClick={() => onPivotClick?.(pg)}>
            <CardTitle title={pg.title}>{pg.title}</CardTitle>
            {pg.obstacles > 0 && (
              <CountBadge $color={COLORS.obstacle}>
                <AlertTriangle size={12} />
                {pg.obstacles}
              </CountBadge>
            )}
            {pg.opportunities > 0 && (
              <CountBadge $color={COLORS.opportunity}>
                <Lightbulb size={12} />
                {pg.opportunities}
              </CountBadge>
            )}
            {(pg.qualityScoredCount > 0 || pg.researchScoredCount > 0) && (
              <QualityBadge>
                {pg.qualityScoredCount > 0 && t('lenses.qualityScore', { score: pg.avgQuality })}
                {pg.qualityScoredCount > 0 && pg.researchScoredCount > 0 && ' · '}
                {pg.researchScoredCount > 0 && t('lenses.researchSupport', {
                  score: `${pg.avgResearch >= 0 ? '+' : ''}${pg.avgResearch}`,
                })}
              </QualityBadge>
            )}
            <ChevronIcon>
              <ChevronRight size={16} />
            </ChevronIcon>
          </CardRow>
        ))}
      </CardsStack>
    </div>
  );
}
